import { toExperimentMap } from '@data/api/mappers/experiments/toExperimentMap'
import { RequestInfo, requester } from '@data/api/requester'
import { getExperimentParticipationQueryKey } from '@data/queries/experiment/getExperimentQueryKey'
import { getPartialExperimentQueryOptions } from '@data/queries/experiment/getPartialExperimentQueryOptions'
import {
    ExperimentParticipationQueryKey,
    ExperimentQueryOptions,
    QueryResultWithSegment,
} from '@data/queries/experiment/types'
import { useExperimentSegmentValues } from '@data/queries/experiment/useExperimentSegmentValues'
import { QueryFunction, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

const experimentParticipationRequest = (experimentName: TExperimentName): RequestInfo<TExperimentMap> => ({
    method: 'GET',
    url: '/experiment/participation',
    params: {
        experiments: [experimentName],
    },
    responseMapper: toExperimentMap,
})

export const queryFn: QueryFunction<TExperimentMap, ExperimentParticipationQueryKey> = ({ queryKey }) =>
    requester(experimentParticipationRequest(queryKey[1].experimentName))

/**
 * Gets the segment for a given experiment.
 * Will not segment the user.
 */
export const useExperimentParticipation = (
    experimentName: TExperimentName,
    options?: ExperimentQueryOptions<ExperimentParticipationQueryKey>
): QueryResultWithSegment => {
    const queryKey = getExperimentParticipationQueryKey(experimentName)
    const combinedOptions = useMemo(
        () => getPartialExperimentQueryOptions(experimentName, options),
        [experimentName, options]
    )
    const queryResult = useQuery<TExperimentMap, ApiError, TExperimentMap, ExperimentParticipationQueryKey>({
        ...combinedOptions,
        queryKey,
        queryFn,
    })

    return useExperimentSegmentValues({ experimentName, queryResult })
}
